import { ChatBubbleBottomCenterTextIcon, XMarkIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { MessageAlert } from 'app/hooks/useNewMessageAlerts'

/**
 * Long enough to read a name, a location, and a message, then decide. The 3s used
 * by the app's ordinary toasts is fine for "Saved" and far too short for this.
 */
export const ALERT_DURATION_MS = 8000

const TICK_MS = 250

/** Matches the CSS transition below, so the row finishes collapsing before unmount. */
export const ALERT_EXIT_MS = 200

function initialsFor(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2)
  return parts
    .map((part) => part[0]!.toUpperCase())
    .join(``)
    .slice(0, 2)
}

export function MessageAlertCard({
  alert,
  contactName,
  locationName,
  isExiting,
  onOpen,
  onDismiss,
}: {
  alert: MessageAlert
  contactName?: string
  locationName?: string
  /** True while the browser tab is hidden. See `MessageAlertStack`. */
  isExiting: boolean
  onOpen: () => void
  onDismiss: () => void
}): ReactElement {
  const [hovered, setHovered] = useState(false)
  const [entered, setEntered] = useState(false)
  const pausedRef = useRef(false)
  const onDismissRef = useRef(onDismiss)

  useEffect(() => {
    pausedRef.current = hovered
  }, [hovered])

  useEffect(() => {
    onDismissRef.current = onDismiss
  }, [onDismiss])

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    let remaining = ALERT_DURATION_MS
    const intervalId = setInterval(() => {
      // Hovering pauses so the card cannot vanish mid-read, and a hidden tab
      // pauses so a backgrounded burst is still there when the user returns.
      if (pausedRef.current) return
      remaining -= TICK_MS
      if (remaining > 0) return
      clearInterval(intervalId)
      onDismissRef.current()
    }, TICK_MS)
    return () => clearInterval(intervalId)
  }, [])

  const hasContact = !!contactName
  const formattedPhone = formatPhoneNumber(alert.phone) ?? alert.phone
  const displayName = hasContact ? contactName! : formattedPhone || `Unknown number`
  const initials = hasContact ? initialsFor(contactName!) : ``

  return (
    <div
      className={classNames(
        `overflow-hidden transition-all duration-200 ease-out`,
        isExiting ? `w-0 opacity-0` : `w-80 opacity-100`,
      )}
    >
      <div
        className={classNames(
          // Always dark. A card matching a light page was genuinely easy to miss, and the
          // page's opposite is a white slab on a dark app, so dark lifts to the raised surface.
          `relative w-80 overflow-hidden rounded-lg bg-inverse shadow-2xl ring-1 ring-strong transition-all duration-200 ease-out dark:bg-surface-raised`,
          entered && !isExiting ? `translate-y-0 opacity-100` : `translate-y-2 opacity-0`,
        )}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Every role inside is written against the dark card, so the light values
            are the right ones in both themes. */}
        <div className="theme-light">
          {/* Countdown. Pauses with the dismiss timer, so what you see is how long
              the card actually has left. */}
          <div className="absolute inset-x-0 top-0 h-1 bg-surface/10">
            <div
              className="animate-alert-countdown h-full bg-primary-400"
              style={{
                animationDuration: `${ALERT_DURATION_MS}ms`,
                animationPlayState: hovered ? `paused` : `running`,
              }}
            />
          </div>
          {/* Same strip as IncomingCallCard's "Incoming call", so the two cards in the row
              say what they are. No icon here: the avatar below already carries the text bubble. */}
          <div className="absolute inset-x-0 top-1 px-3 pt-2 text-label-xs font-medium text-primary-200">New text</div>

          <Button
            type="button"
            variant="ghost"
            onClick={onOpen}
            className="flex h-auto w-full items-start justify-start gap-x-3 whitespace-normal rounded-none border-0 p-3 pr-8 pt-8 text-left hover:bg-surface/5 focus-visible:bg-surface/5"
          >
            <span
              className={classNames(
                `flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-label-xs font-semibold`,
                hasContact ? `bg-primary-400 text-on-light-fill` : `bg-surface/10 text-inverse`,
              )}
            >
              {initials || <ChatBubbleBottomCenterTextIcon className="h-4 w-4" strokeWidth={2} />}
            </span>

            <span className="flex min-w-0 flex-1 flex-col gap-y-0.5">
              <span className="flex min-w-0 items-center gap-x-1.5">
                <span className="min-w-0 truncate text-heading-xs font-semibold text-inverse">{displayName}</span>
                {/* A first-time texter has no contact record at all. That is usually
                    the most valuable alert on the screen (a new lead), so it is
                    marked rather than left as a bare number. */}
                {!hasContact && (
                  <span className="shrink-0 rounded bg-primary-400/20 px-1.5 py-0.5 text-label-xs font-medium text-primary-200">
                    New number
                  </span>
                )}
              </span>
              {locationName && <span className="truncate text-label-xs text-inverse/50">{locationName}</span>}
              <span className="line-clamp-2 text-body-xs leading-snug text-inverse">{alert.text}</span>
            </span>
          </Button>

          <Button
            type="button"
            variant="icon"
            size="icon-sm"
            onClick={onDismiss}
            aria-label="Dismiss notification"
            className="absolute right-1.5 top-2.5 text-inverse/40 hover:bg-surface/10 hover:text-inverse focus-visible:bg-surface/10 focus-visible:text-inverse"
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
